import { createSlice } from "@reduxjs/toolkit";
import type { PayableAmount } from "@/services/leasing/leaseJourney/apiService.service";

const getInitialFileDetails = () => ({
  file: undefined,
  fileName: "",
  downloadUrl: "",
});

// Helper function to create dynamic form state based on document list
const createDynamicFormState = (docList: any[] = []) => {
  const values: Record<string, any> = {};
  const loading: Record<string, boolean> = {};
  const errors: Record<string, boolean | string> = {};

  docList.forEach((doc) => {
    const fieldName = doc.DocId; // Use DocId as the field name directly
    values[fieldName] = {
      ...getInitialFileDetails(),
      docId: doc.DocId,
      docType: doc.DocType,
      status: doc.Status,
      mandatory: doc.mandatory,
      docGeneratedDate: doc.DocGeneratedDate,
    };
    loading[fieldName] = false;
    errors[fieldName] = false;
  });

  return { values, loading, errors };
};

let initialState = {
  loading: false,
  prospectiveUnits: [],
  leasedUnits: [],
  moveinDetails: {
    showReadyToMoveIn: false,
    readyToMoveInUnits: [],
  },
  prospectiveJourneyStatusLoading: false,
  prospectiveJourneyStatus: [],
  unitJourneyStatusLoading: false,
  unitJourneyStatus: {},
  offerLetterPdfBase64: null as string | null,
  leaseAgreementPdfBase64: null as string | null,
  leaseAgreementLoading: false,
  selectedUnit: {},
  documentsUploadFormState: {
    values: {},
    loading: {},
    errors: {},
  },
  // UnitsNotAvailable drawer state
  unitsNotAvailable: {
    isOpen: false,
    selectedUnit: null as any,
    managers: [] as any[],
    title: "",
    description: "",
    loading: false,
    unavailableUnitsData: null as any,
  },
  payableAmount: null as PayableAmount | null,
  paymentLifeCycle: {
    isLoading: false,
    data: {},
  },
  alternativeUnits: {
    isLoading: false,
    data: [] as any[],
  },
};

const leadToleaseJourneySlice = createSlice({
  name: "leadToleaseJourney",
  initialState: initialState,
  reducers: {
    setLeadToleaseJourney: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    setOfferLetterPdfBase64: (state: any, action) => {
      state.offerLetterPdfBase64 = action.payload ?? null;
    },
    setLeaseAgreementPdfBase64: (state: any, action) => {
      state.leaseAgreementPdfBase64 = action.payload ?? null;
    },
    setLeaseAgreementLoading: (state: any, action) => {
      state.leaseAgreementLoading = action.payload ?? null;
    },
    resetLeadToleaseJourney: () => {
      return initialState;
    },
    setLeaseDocUploadFormValues: (state: any, action) => {
      const { fieldName, fieldValue } = action.payload;
      state.documentsUploadFormState.values[fieldName] = {
        ...(state.documentsUploadFormState.values[fieldName] || {}),
        ...(fieldValue || {}),
      };
    },
    setLeaseDocUploadFormLoading: (state: any, action) => {
      const { fieldName, fieldValue } = action.payload;
      state.documentsUploadFormState.loading[fieldName] = fieldValue;
    },
    setLeaseDocUploadFormErrors: (state: any, action) => {
      const { fieldName, fieldValue } = action.payload;
      state.documentsUploadFormState.errors[fieldName] = fieldValue;
    },
    initializeDocumentFormState: (state: any, action) => {
      const docList = action.payload;
      const dynamicFormState = createDynamicFormState(docList);
      state.documentsUploadFormState = dynamicFormState;
    },
    updateDocumentFormStateFromDocList: (state: any, action) => {
      const docList = action.payload;
      const currentFormState = state.documentsUploadFormState;
      
      // Create new form state based on current docList
      const newFormState = createDynamicFormState(docList);
      
      // Preserve existing values if they exist
      Object.keys(newFormState.values).forEach(fieldName => {
        if (currentFormState.values[fieldName]) {
          newFormState.values[fieldName] = {
            ...newFormState.values[fieldName],
            ...currentFormState.values[fieldName],
          };
        }
        if (currentFormState.loading[fieldName] !== undefined) {
          newFormState.loading[fieldName] = currentFormState.loading[fieldName];
        }
        if (currentFormState.errors[fieldName] !== undefined) {
          newFormState.errors[fieldName] = currentFormState.errors[fieldName];
        }
      });
      
      state.documentsUploadFormState = newFormState;
    },
    // UnitsNotAvailable drawer actions
    openUnitsNotAvailableDrawer: (state: any, action) => {
      const { selectedUnit, managers, title, description, unavailableUnitsData } = action.payload;
      state.unitsNotAvailable.isOpen = true;
      state.unitsNotAvailable.selectedUnit = selectedUnit || null;
      state.unitsNotAvailable.managers = managers || [];
      state.unitsNotAvailable.title = title || "";
      state.unitsNotAvailable.description = description || "";
      state.unitsNotAvailable.unavailableUnitsData = unavailableUnitsData || null;
    },
    closeUnitsNotAvailableDrawer: (state: any) => {
      state.unitsNotAvailable.isOpen = false;
      state.unitsNotAvailable.selectedUnit = null;
    },
    setUnitsNotAvailableLoading: (state: any, action) => {
      state.unitsNotAvailable.loading = action.payload;
    },
    setPaymentLifeCycle: (state: any, action) => {
      state.paymentLifeCycle = {
        ...state.paymentLifeCycle,
        ...action.payload,
      };
    },
    setAlternativeUnits: (state: any, action) => {
      state.alternativeUnits = {
        ...state.alternativeUnits,
        ...action.payload,
      };
    },
    setPayableAmount: (state, action: { payload: PayableAmount | null }) => {
      state.payableAmount = action.payload;
    },
  },
});

export const {
  setLeadToleaseJourney,
  setOfferLetterPdfBase64,
  setLeaseAgreementPdfBase64,
  setLeaseAgreementLoading,
  resetLeadToleaseJourney,
  setLeaseDocUploadFormValues,
  setLeaseDocUploadFormLoading,
  setLeaseDocUploadFormErrors,
  initializeDocumentFormState,
  updateDocumentFormStateFromDocList,
  openUnitsNotAvailableDrawer,
  closeUnitsNotAvailableDrawer,
  setUnitsNotAvailableLoading,
  setPaymentLifeCycle,
  setAlternativeUnits,
  setPayableAmount,
} = leadToleaseJourneySlice.actions;
export default leadToleaseJourneySlice.reducer;
